import { useEffect, useMemo, useState } from 'react';
import { LoaderCircle, Plus, Search, Trash2 } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useCreatePurchaseOrder } from '@/api/hooks/usePurchaseOrders';
import { useMedicines } from '@/api/hooks/useMedicine';
import { useSuppliers } from '@/api/hooks/useSuppliers';
import type { PurchaseOrderLineRequest } from '@/api/purchasing';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { money } from '@/lib/utils';

interface DraftLine extends PurchaseOrderLineRequest {
  key: string;
}

export interface ReorderPrefill {
  id: string;
  name: string;
  costPrice?: number | null;
  supplierId?: string;
  suggestedQty?: number;
}

export function NewPurchaseOrderDialog({
  open,
  onOpenChange,
  prefillMedicine,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Arriving from "reorder suggestions" — pre-adds one line and, when known, the supplier. */
  prefillMedicine?: ReorderPrefill;
}) {
  const { data: suppliers } = useSuppliers();
  const { data: catalogue } = useMedicines({ limit: 2000 }, open);
  const createPO = useCreatePurchaseOrder();

  const [supplierId, setSupplierId] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [customName, setCustomName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-seeds every time the dialog opens with a prefill, rather than only at
  // first mount — the dialog stays mounted between "Order" clicks on
  // different reorder-suggestion rows.
  useEffect(() => {
    if (!open) return;

    if (prefillMedicine) {
      setSupplierId(prefillMedicine.supplierId ?? '');
      setLines([
        {
          key: crypto.randomUUID(),
          medicineId: prefillMedicine.id,
          medicineName: prefillMedicine.name,
          qty: prefillMedicine.suggestedQty ?? 10,
          unitCost: prefillMedicine.costPrice ?? 0,
        },
      ]);
    } else {
      setSupplierId('');
      setLines([]);
    }
    setNotes('');
    setSearch('');
    setCustomName('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillMedicine?.id]);

  const debouncedSearch = useDebouncedValue(search, 150);

  const results = useMemo(() => {
    if (!catalogue || debouncedSearch.trim().length === 0) return [];
    const query = debouncedSearch.trim().toLowerCase();
    return catalogue.items.filter((m) => m.name.toLowerCase().includes(query)).slice(0, 10);
  }, [catalogue, debouncedSearch]);

  function addLine(line: Omit<DraftLine, 'key'>) {
    setLines((prev) => [...prev, { ...line, key: crypto.randomUUID() }]);
    setSearch('');
  }

  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }

  async function handleSubmit() {
    setError(null);

    if (!supplierId) {
      setError('Select a supplier.');
      return;
    }
    if (lines.length === 0) {
      setError('Add at least one item.');
      return;
    }
    if (lines.some((l) => !l.qty || l.qty < 1)) {
      setError('Every line needs a quantity of at least 1.');
      return;
    }

    try {
      await createPO.mutateAsync({
        supplierId,
        items: lines.map(({ key: _key, ...line }) => line),
        notes: notes.trim() || undefined,
      });
      toast({ title: 'Purchase order created', variant: 'success' });
      onOpenChange(false);
      setLines([]);
      setSupplierId('');
      setNotes('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New purchase order</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Supplier *</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a supplier" />
              </SelectTrigger>
              <SelectContent>
                {suppliers?.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Add items</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search existing medicines…"
                className="pl-9"
              />
            </div>

            {results.length > 0 && (
              <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border border-border p-1">
                {results.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => addLine({ medicineId: m.id, medicineName: m.name, qty: 10, unitCost: m.costPrice ?? m.price })}
                    className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-secondary"
                  >
                    <span className="truncate">{m.name}</span>
                    <span className="tabular text-xs text-muted-foreground">Stock: {m.stock}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="…or type a new item not yet in your catalogue"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                disabled={!customName.trim()}
                onClick={() => {
                  addLine({ medicineName: customName.trim(), qty: 10, unitCost: 0 });
                  setCustomName('');
                }}
              >
                <Plus /> Add
              </Button>
            </div>
          </div>

          {lines.length > 0 && (
            <div className="space-y-2 rounded-md border border-border p-2">
              {lines.map((line) => (
                <div key={line.key} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm">{line.medicineName}</span>
                  <Input
                    type="number"
                    min={1}
                    value={line.qty}
                    onChange={(e) => updateLine(line.key, { qty: Number(e.target.value) || 0 })}
                    className="h-8 w-16 text-center"
                    aria-label={`Quantity for ${line.medicineName}`}
                  />
                  <div className="relative w-24">
                    <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      ₹
                    </span>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={line.unitCost}
                      onChange={(e) => updateLine(line.key, { unitCost: Number(e.target.value) || 0 })}
                      className="h-8 pl-5"
                      aria-label={`Unit cost for ${line.medicineName}`}
                    />
                  </div>
                  <span className="tabular w-20 shrink-0 text-right text-xs text-muted-foreground">
                    {money(line.qty * line.unitCost)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-status-expired"
                    onClick={() => removeLine(line.key)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="po-notes">Notes</Label>
            <Input id="po-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={createPO.isPending}>
            {createPO.isPending && <LoaderCircle className="animate-spin" />}
            Create purchase order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
